import {
  applyAction,
  cardCode,
  currentChainScore,
  forwardSign,
  homeRow,
  legalActions,
  maxLinks,
  opponent,
  type Action,
  type MatchState,
  type PlayLinkAction,
  type Side,
} from '../engine';
import {
  epitaphValue,
  heldEpitaphValue,
  positional,
  threatLoss,
  topControl,
  type EvalContext,
} from './valuation';

/** A complete turn: the actions to play and the state once they have resolved. */
export interface Plan {
  readonly actions: readonly Action[];
  readonly end: MatchState;
  readonly kind: 'chain' | 'epitaph' | 'recover';
  readonly cardsSpent: number;
  readonly wildsSpent: number;
  readonly captures: number;
  /** Points scored by the planning side during the plan. */
  readonly gain: number;
  value: number;
}

const isLink = (a: Action): a is PlayLinkAction => a.type === 'PLAY_LINK';

const targetKey = (a: PlayLinkAction): string =>
  'to' in a.target
    ? `${a.target.to.x}${a.target.to.y}`
    : 'hops' in a.target
      ? a.target.hops.map((h) => `${h.x}${h.y}`).join('.')
      : `trick:${a.target.trick.pawnId ?? ''}:${a.target.trick.face ? `${a.target.trick.face.color}${a.target.trick.face.number}` : ''}`;

/** Cost of pawning a pawn: prefer home-row, uncrowned, unadvanced pawns and reserves. */
function collateralCost(s: MatchState, pawnId: string): number {
  const p = s.pawns[pawnId];
  if (!p || !p.square) return -1; // a reserve: the board is untouched
  const adv = (p.square.y - homeRow(p.side)) * forwardSign(p.side);
  return (p.crowned ? 6 : 0) + adv * 0.6 + (p.inscription ? 0.5 : 0);
}

/** Legal link actions with interchangeable cards merged and one collateral kept per Wild move. */
export function candidateLinks(s: MatchState, me: Side): PlayLinkAction[] {
  const best = new Map<string, { a: PlayLinkAction; cost: number }>();
  for (const a of legalActions(s, me)) {
    if (!isLink(a)) continue;
    const card = s.cards[a.cardId];
    if (!card) continue;
    if (a.wild) {
      const key = `W|${a.pawnId ?? ''}|${targetKey(a)}|${a.wild.face.color}${a.wild.face.number}`;
      const cost = collateralCost(s, a.wild.collateralPawnId);
      const prior = best.get(key);
      if (!prior || cost < prior.cost) best.set(key, { a, cost });
    } else {
      const key = `${cardCode(card)}|${a.pawnId ?? ''}|${targetKey(a)}`;
      if (!best.has(key)) best.set(key, { a, cost: 0 });
    }
  }
  return [...best.values()].map((b) => b.a);
}

/** Resolves decisions that follow an action until another turn starts (inscription and Epitaph choices). */
export function settle(s: MatchState, me: Side, ctx: EvalContext): MatchState {
  let state = s;
  for (let guard = 0; guard < 6; guard++) {
    if (state.phase === 'CHOOSE_INSCRIPTION') {
      const replace = preferNewInscription(state, ctx);
      const r = applyAction(state, { type: 'CHOOSE_INSCRIPTION', side: state.decider, replace });
      if (r.error) break;
      state = r.state;
    } else if (state.phase === 'CHOOSE_EPITAPH' && state.pendingEpitaphChoice) {
      const owner = state.pendingEpitaphChoice.owner;
      let keep = state.pendingEpitaphChoice.candidates[0]?.id ?? '';
      let bestValue = -Infinity;
      for (const e of state.pendingEpitaphChoice.candidates) {
        if (state.pendingEpitaphChoice.kept.includes(e.id)) continue;
        const value = epitaphValue(state, owner, e.inscription);
        if (value > bestValue) {
          bestValue = value;
          keep = e.id;
        }
      }
      const r = applyAction(state, { type: 'CHOOSE_EPITAPH', side: owner, keepEpitaphId: keep });
      if (r.error) break;
      state = r.state;
    } else break;
  }
  void me;
  return state;
}

/** Inscription choice (§10.1): keep the route that would make the better Epitaph for its owner. */
export function preferNewInscription(s: MatchState, ctx: EvalContext): boolean {
  const pending = s.pendingInscription;
  if (!pending) return false;
  const pawn = s.pawns[pending.pawnId];
  if (!pawn?.inscription) return true;
  const side = pawn.side;
  const current = epitaphValue(s, side, pawn.inscription);
  const next = epitaphValue(s, side, pending.inscription);
  void ctx;
  return next > current + 0.01;
}

/** Full evaluation of a plan's end state from the planning side's point of view. */
export function evaluatePlan(start: MatchState, plan: Plan, ctx: EvalContext): number {
  const { me, opp, personality, settings } = ctx;
  const end = plan.end;
  let v = plan.gain;
  v -= plan.cardsSpent * ctx.cardValue + plan.wildsSpent * ctx.cardValue * 0.7;
  v += (personality.aggression - 1) * plan.captures * start.config.constants.POINTS_CAPTURE;
  // Variant B: a pending crown bonus is worth its points if the pawn survives.
  const pendingBefore = start.players[me].pendingCrownBonuses.length;
  const pendingAfter = end.players[me].pendingCrownBonuses.length;
  if (pendingAfter > pendingBefore)
    v += (pendingAfter - pendingBefore) * start.config.constants.POINTS_CROWN * 0.6;
  v += positional(end, me) - positional(start, me);
  v -= positional(end, opp) - positional(start, opp);
  // Pawning a pawn for a Wild takes it off the board for the round.
  if (settings.tactical) {
    v -= personality.caution * threatLoss(end, ctx);
    // Captured inscribed pawns hand their routes to the opponent (§10.2).
    const giftBefore = heldEpitaphValue(start, opp);
    const giftAfter = heldEpitaphValue(end, opp);
    v -= personality.epitaphRegard * Math.max(0, giftAfter - giftBefore);
    // Spending our own Epitaph uses up its future value.
    const ownBefore = heldEpitaphValue(start, me);
    const ownAfter = heldEpitaphValue(end, me);
    if (ownAfter < ownBefore) v -= 0.3 * personality.epitaphRegard * (ownBefore - ownAfter);
    v += topControl(end, ctx);
  }
  return v;
}

interface Node {
  readonly state: MatchState;
  readonly actions: Action[];
  readonly quick: number;
}

function planFrom(
  start: MatchState,
  actions: Action[],
  end: MatchState,
  me: Side,
  kind: Plan['kind'],
): Plan {
  let cards = 0;
  let wilds = 0;
  for (const a of actions) {
    if (a.type === 'PLAY_LINK') {
      cards++;
      if (a.wild) wilds++;
    } else if (a.type === 'RECOVER') cards++;
  }
  const oppBefore = Object.values(start.pawns).filter(
    (p) => p.side === opponent(me) && p.status === 'onBoard',
  ).length;
  const oppAfter = Object.values(end.pawns).filter(
    (p) => p.side === opponent(me) && p.status === 'onBoard',
  ).length;
  return {
    actions,
    end,
    kind,
    cardsSpent: cards,
    wildsSpent: wilds,
    captures: Math.max(0, oppBefore - oppAfter),
    gain: end.players[me].roundScore - start.players[me].roundScore,
    value: 0,
  };
}

export interface SearchResult {
  readonly plans: Plan[];
  readonly nodes: number;
}

/**
 * The highest-scoring chain from an ACTION or CHAIN state, by depth-first search over every
 * distinct link within `budget` simulated links. The beam search ranks openers by what they score
 * on their own, so it can drop one that sets up a big finish, most often when a Wild multiplies
 * the choices.
 */
function topScoringChain(
  root: MatchState,
  me: Side,
  budget: number,
): { actions: Action[]; state: MatchState; score: number; nodes: number } {
  let nodes = 0;
  let best = { actions: [] as Action[], state: root, score: 0 };
  const visit = (s: MatchState, path: Action[]): void => {
    for (const a of candidateLinks(s, me)) {
      if (nodes >= budget) return;
      nodes++;
      const r = applyAction(s, a);
      if (r.error) continue;
      const actions = [...path, a];
      const open = r.state.phase === 'CHAIN' && r.state.decider === me;
      const score = open
        ? (currentChainScore(r.state)?.score ?? 0)
        : r.state.players[me].roundScore - root.players[me].roundScore;
      if (score > best.score) best = { actions, state: r.state, score };
      if (open) visit(r.state, actions);
    }
  };
  visit(root, []);
  return { ...best, nodes };
}

/**
 * Enumerates complete turns from an ACTION or CHAIN state: chains (beam search over links),
 * Epitaph replays and Recovers. Returns every plan with its evaluated value, best first.
 */
export function searchPlans(root: MatchState, ctx: EvalContext): SearchResult {
  const me = ctx.me;
  const s = ctx.settings;
  const plans: Plan[] = [];
  let nodes = 0;
  const finish = (actions: Action[], state: MatchState, kind: Plan['kind']) => {
    const end = settle(state, me, ctx);
    plans.push(planFrom(root, actions, end, me, kind));
  };

  // Level 0: END_CHAIN is a complete plan when a chain is already in progress.
  if (root.phase === 'CHAIN') {
    const r = applyAction(root, { type: 'END_CHAIN', side: me });
    nodes++;
    if (!r.error) finish([{ type: 'END_CHAIN', side: me }], r.state, 'chain');
  } else {
    const recovered = new Set<string>();
    for (const a of legalActions(root, me)) {
      if (a.type !== 'PLAY_EPITAPH' && a.type !== 'RECOVER') continue;
      // A replay that opens a chain (Séance Table) is searched as an ordinary replay.
      if (a.type === 'PLAY_EPITAPH' && a.chain) continue;
      if (a.type === 'RECOVER') {
        // Recovering either copy of a face is equivalent; keep one per face.
        const card = root.cards[a.cardId];
        const code = card ? cardCode(card) : a.cardId;
        if (recovered.has(code)) continue;
        recovered.add(code);
      }
      const r = applyAction(root, a);
      nodes++;
      if (!r.error) finish([a], r.state, a.type === 'RECOVER' ? 'recover' : 'epitaph');
    }
  }

  let frontier: Node[] = [{ state: root, actions: [], quick: 0 }];
  const beams = [s.beam1, s.beam2, Infinity];
  const played = root.phase === 'CHAIN' ? (root.chain?.links.length ?? 0) : 0;
  const depthLimit = Math.min(maxLinks(root, me), (s.maxChain ?? Infinity) - played);
  for (let depth = 0; depth < depthLimit && frontier.length > 0; depth++) {
    const next: Node[] = [];
    for (const node of frontier) {
      if (nodes >= s.nodeBudget) break;
      for (const a of candidateLinks(node.state, me)) {
        if (nodes >= s.nodeBudget) break;
        const r = applyAction(node.state, a);
        nodes++;
        if (r.error) continue;
        const actions = [...node.actions, a];
        if (r.state.phase === 'CHAIN' && r.state.decider === me) {
          const chainScore = currentChainScore(r.state)?.score ?? 0;
          const quick = chainScore - actions.length * ctx.cardValue - (a.wild ? ctx.cardValue : 0);
          next.push({ state: r.state, actions, quick });
        } else {
          finish(actions, r.state, 'chain');
        }
      }
    }
    // Every open chain can also end here.
    next.sort((x, y) => y.quick - x.quick);
    const kept = next.slice(0, beams[depth] ?? Infinity);
    for (const node of next) {
      const r = applyAction(node.state, { type: 'END_CHAIN', side: me });
      nodes++;
      if (!r.error) finish([...node.actions, { type: 'END_CHAIN', side: me }], r.state, 'chain');
    }
    frontier = kept;
  }

  if (s.chainSearch > 0) {
    const top = topScoringChain(root, me, s.chainSearch);
    nodes += top.nodes;
    const beamBest = plans.reduce((m, p) => (p.kind === 'chain' ? Math.max(m, p.gain) : m), 0);
    if (top.score > beamBest) {
      let { state } = top;
      const actions = [...top.actions];
      if (state.phase === 'CHAIN' && state.decider === me) {
        const r = applyAction(state, { type: 'END_CHAIN', side: me });
        nodes++;
        if (!r.error) {
          actions.push({ type: 'END_CHAIN', side: me });
          state = r.state;
        }
      }
      finish(actions, state, 'chain');
    }
  }

  for (const p of plans) p.value = evaluatePlan(root, p, ctx);
  plans.sort((a, b) => b.value - a.value);
  return { plans, nodes };
}
